import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AlertTriangle, Trash2, Undo2, Lock, LockOpen } from "lucide-react";
import LeadDetailModal from "./LeadDetailModal";
import AssignModal from "./AssignModal";
import { useCanManageLeads } from "./permissions";

// Copied from AdminDashboard.jsx:1784-1785 — the other tabs still use those.
export const Th = ({ children }) => <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{children}</th>;
export const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;

export default function LeadsTab() {
  const canManage = useCanManageLeads();
  const [leads, setLeads] = useState([]);
  const [partners, setPartners] = useState([]);
  const [openLead, setOpenLead] = useState(null);
  const [detailLeadId, setDetailLeadId] = useState(null);
  const [filters, setFilters] = useState({ status: "", mobile: "", include_deleted: false });

  const refresh = () => {
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.mobile) params.mobile = filters.mobile;
    if (filters.include_deleted) params.include_deleted = true;
    api.get("/admin/leads", { params }).then(({ data }) => setLeads(data));
    api.get("/admin/partners?status=approved").then(({ data }) => setPartners(data));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [filters.status, filters.mobile, filters.include_deleted]);

  const close = async (l) => {
    const reason = window.prompt(`Close "${l.name}"?\n\nOptional reason:`);
    if (reason === null) return;
    try {
      const { data } = await api.post(`/admin/leads/${l.id}/close`, { reason });
      if (data.warnings?.length) {
        window.alert("Closed.\n\n" + data.warnings.join("\n"));
      }
      refresh();
    } catch (err) {
      window.alert(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const reopen = async (l) => {
    try { await api.post(`/admin/leads/${l.id}/reopen`); refresh(); }
    catch (err) { window.alert(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete "${l.name}"?\n\nThe lead is hidden, not destroyed — assignments and contractor work history are kept, and you can restore it.`)) return;
    try { await api.delete(`/admin/leads/${l.id}`); refresh(); }
    catch (err) { window.alert(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const restore = async (l) => {
    try { await api.post(`/admin/leads/${l.id}/restore`); refresh(); }
    catch (err) { window.alert(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="mt-4 bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="p-4 border-b border-slate-200 flex flex-wrap gap-3 items-end">
        <label className="flex flex-col">
          <span className="text-xs text-slate-500">Status</span>
          <select className="nc-input py-2" value={filters.status}
                  onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
                  data-testid="lead-filter-status">
            <option value="">Open leads</option>
            <option value="new">New</option>
            <option value="assigned">Assigned</option>
            <option value="closed">Closed</option>
          </select>
        </label>
        <label className="flex flex-col">
          <span className="text-xs text-slate-500">Mobile</span>
          <input className="nc-input py-2" value={filters.mobile} placeholder="98765xxxxx"
                 onChange={e => setFilters(f => ({ ...f, mobile: e.target.value.trim() }))}
                 data-testid="lead-filter-mobile" />
        </label>
        {canManage && (
          <label className="flex items-center gap-2 text-sm text-slate-600 pb-2">
            <input type="checkbox" checked={filters.include_deleted}
                   onChange={e => setFilters(f => ({ ...f, include_deleted: e.target.checked }))}
                   data-testid="lead-filter-deleted" />
            Show deleted
          </label>
        )}
        {(filters.status || filters.mobile || filters.include_deleted) && (
          <button className="text-sm text-emerald-700 font-medium pb-2"
                  onClick={() => setFilters({ status: "", mobile: "", include_deleted: false })}>
            Clear
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr><Th>Name</Th><Th>Mobile</Th><Th>Source</Th><Th>State</Th><Th>Status</Th><Th>Created</Th><Th>Action</Th></tr>
          </thead>
          <tbody>
            {leads.map(l => (
              <tr key={l.id}
                  className={`border-b border-slate-100 hover:bg-slate-50 ${l.is_deleted ? "opacity-50" : ""}`}
                  data-testid={`lead-row-${l.id}`}>
                <Td>
                  <button onClick={() => setDetailLeadId(l.id)}
                          className="text-emerald-700 hover:underline font-medium text-left"
                          data-testid={`lead-name-${l.id}`}>{l.name}</button>
                  {l.is_deleted && <span className="ml-2 text-xs text-rose-600">deleted</span>}
                </Td>
                <Td>
                  {l.mobile}
                  {l.duplicate_count > 1 && (
                    <button
                      onClick={() => setFilters(f => ({ ...f, mobile: l.mobile, status: "" }))}
                      className="ml-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200"
                      title="Show every enquiry from this number"
                      data-testid={`lead-dupe-${l.id}`}>
                      <AlertTriangle className="w-3 h-3" />
                      {l.duplicate_count} leads from this number
                    </button>
                  )}
                </Td>
                <Td><span className="nc-chip">{l.source}</span></Td>
                <Td>{l.state || "-"}</Td>
                <Td>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    l.status === "assigned" ? "bg-emerald-100 text-emerald-700"
                    : l.status === "closed" ? "bg-slate-200 text-slate-600"
                    : "bg-slate-100 text-slate-600"}`}>{l.status}</span>
                </Td>
                <Td>{new Date(l.created_at).toLocaleDateString("en-IN")}</Td>
                <Td>
                  <div className="flex items-center gap-3">
                    {!l.is_deleted && l.status !== "closed" && (
                      <button onClick={() => setOpenLead(l)} className="text-emerald-700 font-medium"
                              data-testid={`assign-lead-${l.id}`}>Assign</button>
                    )}
                    {canManage && !l.is_deleted && (
                      l.status === "closed"
                        ? <button onClick={() => reopen(l)} title="Reopen"
                                  data-testid={`reopen-lead-${l.id}`}><LockOpen className="w-4 h-4 text-slate-500" /></button>
                        : <button onClick={() => close(l)} title="Close"
                                  data-testid={`close-lead-${l.id}`}><Lock className="w-4 h-4 text-slate-500" /></button>
                    )}
                    {canManage && (
                      l.is_deleted
                        ? <button onClick={() => restore(l)} title="Restore"
                                  data-testid={`restore-lead-${l.id}`}><Undo2 className="w-4 h-4 text-slate-500" /></button>
                        : <button onClick={() => remove(l)} title="Delete"
                                  data-testid={`delete-lead-${l.id}`}><Trash2 className="w-4 h-4 text-rose-500" /></button>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
            {leads.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-400">No leads yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {openLead && (
        <AssignModal lead={openLead} partners={partners}
                     onClose={() => setOpenLead(null)} onAssigned={refresh} />
      )}

      {detailLeadId && <LeadDetailModal leadId={detailLeadId} onClose={() => setDetailLeadId(null)} />}
    </div>
  );
}
